<script setup lang="ts">
import { Planungsgeschaeft } from '@/models/Planungsgeschaeft';
import {
    createValidator,
    createValidators,
    validateMaxLength,
    validateNotEmpty
} from '@kp/geofy-vuejs-lib/tools/model_validators.js';
import { useGemeindeModels } from '@/composables/useGemeindeCollection';
import { computed, inject, onMounted, ref, type Ref } from 'vue';
import { globalVar } from '@/helpers/globals';
import KpDropdown from '@kp/geofy-vuejs-lib/tg-system/components/KpDropdown.vue';
import {
    PlanningPermissionApplication,
    PlanningPermissionApplicationCollection
} from '@/models/PlanningPermissionApplication';
import { getI18n } from '@/helpers/i18n';
import PrevNextBtnBar from '@/components/PrevNextBtnBar.vue';
import Download from '@/components/Download.vue';
import { hasPpaReachedSubmitRoute } from '@/helpers/tools';
import { usePGApplicationTypes } from '@/composables/usePGApplicationTypes';
import { useBusinessTypes } from '@/composables/useBusinessTypeCollection';
import type { ApplicationType } from '@/models/ApplicationType';
import type { BusinessType } from '@/models/BusinessType';
import type {
    KTGDropdownAutocompleteInputEvent,
    KTGDropdownAutocompleteSearchInputEvent
} from '@kanton-thurgau/design-system/dropdown';

// injected from PlanungsgeschaeftEditView
const saving = inject('saving') as Ref<boolean>;
const readonly = inject('readonly') as Ref<boolean>;
const saveGeschaeft = inject('saveGeschaeft') as () => Promise<void>;
const gemeinden = useGemeindeModels();


const props = defineProps<{
    geschaeft: Planungsgeschaeft;
}>();

const environment = computed(() => {
    return globalVar('environment');
});

const emit = defineEmits<{
    next: [];
    prev: [];
}>();

const downloader: Ref<InstanceType<typeof Download> | null> = ref(null);


const { validators, validateAll } = createValidators({
    title: createValidator(
        computed(() => props.geschaeft.description),
        [validateNotEmpty, validateMaxLength(100)],
        { initialValidate: false }
    ),
    application_type: createValidator(
        computed(() => props.geschaeft.application_type_id),
        [validateNotEmpty],
        { initialValidate: false }
    ),
    businessType: createValidator(
        computed(() => props.geschaeft.business_type_ids),
        [validateSelectAtLeastOne],
        { initialValidate: false }
    )
});

function validateSelectAtLeastOne(value?: number[] | null): true | string {
    if (!value) {
        return getI18n().t('bitte_mind_1_wert');
    }
    if (value.length > 0) {
        return true;
    }
    return getI18n().t('bitte_mind_1_wert');
}

const filteredGemeinden = computed(() => {
    if (!gemeinden.value) {
        return [];
    }
    return gemeinden.value.filter((item) => {
        return item.town && item.town.length > 0;
    });
});

const pgTypes: Ref<ApplicationType[]> = ref([]);
const businessTypes: Ref<BusinessType[]> = ref([]);

onMounted(async () => {
    if (hasPpaReachedSubmitRoute(props.geschaeft)) {
        validateAll();
    }
    const { collection: atCollection, loading: atLoading } = usePGApplicationTypes();
    await atLoading;
    pgTypes.value = atCollection.getModels();

    const { collection: btCollection, loading: btLoading } = useBusinessTypes();
    await btLoading;
    businessTypes.value = btCollection.getModels();

    loadReferencedProject();
});

async function mutateGeschaeft(field: string, value: unknown) {
    if (readonly.value) {
        return;
    }
    props.geschaeft.set(field, value);
    await saveGeschaeft();
}

function onReset() {
    emit('prev');
}

async function onNextStep() {
    emit('next');
}

function onCreatePdf() {
    downloader?.value?.downloadFile(
        '/Planungsgeschaeft/createPlanungsgeschaeftsformularPdf?id=' + props.geschaeft.id,
        'pdf_wird_erstellt'
    );
}

function toggleBusinessType(isOn: boolean, businessType: BusinessType) {
    if (isOn) {
        mutateGeschaeft(
            'business_type_ids',
            Array.from(new Set([...(props.geschaeft.business_type_ids || []), businessType.id]))
        );
    } else {
        mutateGeschaeft(
            'business_type_ids',
            props.geschaeft.business_type_ids?.filter((i) => i !== businessType.id) || null
        );
    }
}

let ignoreNextQuery = false;
const refProjects = ref<PlanningPermissionApplication[]>([]);
function searchProject(queryEvent: KTGDropdownAutocompleteSearchInputEvent) {
    // Ugly hack, so the search is not triggered with the concatenated string
    if (ignoreNextQuery) {
        ignoreNextQuery = false;
        return;
    }
    const ppaCollection = new PlanningPermissionApplicationCollection();
    ppaCollection.setQueryParams({
        query: queryEvent.searchTerm,
        filter: [
            { id: 'id', property: 'id', value: props.geschaeft?.id, operator: '!=' },
            { id: 'stelle_id', property: 'stelle_id', value: props.geschaeft.stelle_id },
            { id: 'type', property: 'type', value: props.geschaeft.type }
        ]
    });
    ppaCollection.query();
    refProjects.value = ppaCollection.getModels();
}

async function selectProject(event: KTGDropdownAutocompleteInputEvent) {
    await mutateGeschaeft('reference_planning_permission_application_id', event.value);
    loadReferencedProject();
    ignoreNextQuery = true;
}

function loadReferencedProject() {
    if (props.geschaeft.reference_planning_permission_application_id) {
        const ppaCollection = new PlanningPermissionApplicationCollection();
        ppaCollection.setQueryParams({ id: props.geschaeft.reference_planning_permission_application_id });
        ppaCollection.query();
        refProjects.value = ppaCollection.getModels();
    }
}
</script>

<template>
    <div>
        <h4 class="ktg-title-04 ktg-inner-box-padding mb-1_5rem">{{ $t('allgemeine_angaben') }}</h4>
        <fieldset class="ktg-fieldset ktg-form-area-maxwidth py-3_5rem px-6rem">
            <h5 class="ktg-title-05 ktg-inner-box-padding">{{ $t('planungsgeschaeft') }}</h5>
            <ktg-line class="mt-0_5rem mb-1rem"></ktg-line>

            <!-- Gemeinde -->
            <ktg-field>
                <ktg-label>{{ $t('gemeinde') }}</ktg-label>
                <ktg-dropdown-autocomplete
                    :value="geschaeft.stelle_id"
                    class="w-full"
                    name="gemeinde"
                    :label="$t('gemeinde')"
                    trailing-icon="lock-closed"
                    readonly="readonly"
                    :placeholder="$t('nach_gemeinden_suchen')"
                    auto-activate-first
                    @change="mutateGeschaeft('stelle_id', $event.target.value)"
                >
                    <ktg-dropdown-option
                        v-for="gemeinde in filteredGemeinden"
                        :value="String(gemeinde.id)"
                        :key="gemeinde.id"
                        >{{ gemeinde.town }}</ktg-dropdown-option
                    >
                </ktg-dropdown-autocomplete>
            </ktg-field>

            <!-- Title -->
            <ktg-field>
                <ktg-label for="title">{{ $t('title') }} *</ktg-label>
                <ktg-text-input
                    class="w-full"
                    input-id="title"
                    :value="geschaeft.description"
                    type="text"
                    :placeholder="$t('titel_planungsgeschaeft')"
                    name="title"
                    maxlength="100"
                    :readonly="readonly"
                    required
                    error-id="err_title"
                    :error="!validators.title.isValid.value"
                    @change="mutateGeschaeft('description', $event.target.value)"
                    @blur="validators.title.validate()"
                >
                </ktg-text-input>
                <ktg-field-error v-if="!validators.title.isValid.value" id="err_title">{{
                    validators.title.msg.value
                }}</ktg-field-error>
            </ktg-field>

            <!-- Dossier Nr Gemeinde -->
            <ktg-field>
                <ktg-label for="title">{{ $t('dossier_nr_gemeinde') }}</ktg-label>
                <ktg-tooltip-button id="dossier_nr_gemeinde_info"></ktg-tooltip-button>
                <ktg-tooltip for="dossier_nr_gemeinde_info"
                    ><div v-html="$t('tootltip_dossier_nr_gemeinde')"></div
                ></ktg-tooltip>
                <ktg-text-input
                    class="w-full"
                    input-id="dossier_nr_gemeinde"
                    :value="geschaeft.dossier_nr_gemeinde"
                    type="text"
                    :placeholder="$t('dossier_nr_gemeinde')"
                    name="dossier_nr_gemeinde"
                    maxlength="255"
                    :readonly="readonly"
                    @change="mutateGeschaeft('dossier_nr_gemeinde', $event.target.value)"
                >
                </ktg-text-input>
            </ktg-field>

            <!-- Geschäftsart -->
            <ktg-field>
                <ktg-label for="application_type">{{ $t('application_type') }} *</ktg-label>
                <KpDropdown
                    :model-value="geschaeft.application_type_id"
                    :options="pgTypes"
                    display-field="bezeichnung"
                    value-field="id"
                    class="w-full"
                    :readonly="readonly"
                    :emit-value-cast="String"
                    :placeholder="$t('please_chose')"
                    :error="validators.application_type.msg.value"
                    error-id="err_application_type"
                    @blur="validators.application_type.validate()"
                    @update:model-value="(v: any) => geschaeft.set('application_type_id', v)"
                ></KpDropdown>
                <ktg-field-error v-if="!validators.application_type.isValid.value" id="err_application_type">{{
                    validators.application_type.msg.value
                }}</ktg-field-error>
            </ktg-field>

            <!-- Geschäftstyp -->
            <ktg-label class="ml-1rem">{{ $t('business_type') }}: *</ktg-label>
            <div class="business-type-grid ktg-inner-box-padding mb-1rem">
                <ktg-labelled-choice v-for="businessType in businessTypes" :key="businessType.id">
                    <ktg-checkbox
                        :checked="geschaeft.business_type_ids?.includes(businessType.id || 0)"
                        :input-id="businessType.ech_value"
                        :readonly="readonly"
                        @change="toggleBusinessType($event.target?.checked, businessType)"
                    ></ktg-checkbox>
                    <ktg-label :for="businessType.ech_value">{{ businessType.bezeichnung }}</ktg-label>
                </ktg-labelled-choice>
            </div>
            <ktg-field-error
                class="list-error-margin"
                v-if="!validators.businessType.isValid.value"
                id="err_businessType"
                >{{ validators.businessType.msg.value }}</ktg-field-error
            >

            <!-- Referenziertes Projekt -->
            <ktg-field>
                <ktg-label>{{ $t('referenziertes_geschaeft') }}</ktg-label>
                <ktg-tooltip-button id="referenziertes_geschaeft_info"></ktg-tooltip-button>
                <ktg-tooltip for="referenziertes_geschaeft_info"
                    ><div v-html="$t('referenziertes_geschaeft_info')"></div
                ></ktg-tooltip>
                <ktg-dropdown-autocomplete
                    :value="geschaeft.reference_planning_permission_application_id"
                    class="w-full"
                    name="gemeinde"
                    :label="$t('referenziertes_geschaeft')"
                    trailing-icon="search"
                    clearable
                    :readonly="readonly"
                    :placeholder="$t('search_referenziertes_geschaeft')"
                    auto-activate-first
                    @search-input="searchProject"
                    @input="selectProject"
                >
                    <ktg-dropdown-option
                        v-for="refProject in refProjects"
                        :value="String(refProject.id)"
                        :key="refProject.id"
                        >{{ refProject.id }} {{ $t(refProject.application_type || '') }} -
                        {{ refProject.description }}</ktg-dropdown-option
                    >
                </ktg-dropdown-autocomplete>
            </ktg-field>

            <div v-if="environment === 'dev' || environment === 'test'">
                <ktg-line class="my-1rem"></ktg-line>
                <ktg-button appearance="secondary" class="w-full" @click="onCreatePdf">
                    {{ $t('create_pdf') }}
                </ktg-button>

                <Download ref="downloader"></Download>
            </div>

            <ktg-line class="mt-2rem mb-1rem"></ktg-line>
            <PrevNextBtnBar
                :back-text="$t('cancel')"
                :next-text="$t('to_next_step')"
                :disabled="saving"
                @next="onNextStep"
                @back="onReset"
            />
        </fieldset>
    </div>
</template>

<style scoped>
.business-type-grid {
    display: grid;
    gap: 1rem;
    grid-template-columns: 1fr 1fr;
}
</style>
